/**
 * React Query hooks for the Wireline & Slickline AI drafts (WL-6,
 * fieldiq-api#1362). Keys start ['wirelineSlickline'] like every WL hook, so a
 * WL write elsewhere refreshes these too; reads spread
 * LOAD_FAILURE_QUERY_OPTIONS (a 403 is an answer, not a flake). A run, an
 * approve and a dismiss each invalidate the WL prefix — the register, the
 * stored run, and (for a report draft approved into its report) the report.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { LOAD_FAILURE_QUERY_OPTIONS } from '../lib/load-failure';
import { invalidateWl } from './useWirelineSlickline';
import type { WlAiApproveBody, WlAiRunResult, WlAiStoredRun, WlRequiredCertificates } from '../types/wl-ai';

/** `{ data }` → data, or null. Never the envelope. */
function unwrap<T>(body: unknown): T | null {
  if (body === null || typeof body !== 'object' || Array.isArray(body)) return null;
  const d = (body as { data?: unknown }).data;
  return d === undefined || d === null ? null : (d as T);
}

export const wlAiKeys = {
  digests: (agent?: string, jobId?: string, outcome?: string) => ['wirelineSlickline', 'ai', 'digests', agent ?? '', jobId ?? '', outcome ?? ''] as const,
  digest: (id: string) => ['wirelineSlickline', 'ai', 'digest', id] as const,
};

/** `outcome` is agent_runs.outcome, filtered by the API before its 20-row limit. */
export function useWlAiDigests(filters: { agent?: string; jobId?: string; outcome?: string } = {}, enabled = true) {
  const { agent, jobId, outcome } = filters;
  return useQuery({
    queryKey: wlAiKeys.digests(agent, jobId, outcome),
    queryFn: async () => {
      const params: { agent?: string; jobId?: string; outcome?: string } = {};
      if (agent) params.agent = agent;
      if (jobId) params.jobId = jobId;
      if (outcome) params.outcome = outcome;
      const d = unwrap<WlAiStoredRun[]>((await api.wirelineSlickline.ai.digests(Object.keys(params).length ? params : undefined)).data);
      return Array.isArray(d) ? d : [];
    },
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useWlAiDigest(id: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: wlAiKeys.digest(id ?? ''),
    queryFn: async () => unwrap<WlAiStoredRun>((await api.wirelineSlickline.ai.digest(id as string)).data),
    enabled: enabled && !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export type WlAiRunRequest =
  | { agent: 'wl_sheet_summary'; sheetId: string }
  | { agent: 'wl_ticket_preflight'; ticketId: string }
  | { agent: 'wl_ticket_preflight'; jobId: string }
  | { agent: 'wl_service_report_draft'; reportId: string }
  | { agent: 'wl_compliance_digest' };

export function useRunWlAi() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (req: WlAiRunRequest): Promise<WlAiRunResult | null> => {
      const ai = api.wirelineSlickline.ai;
      let res;
      if (req.agent === 'wl_sheet_summary') res = await ai.sheetSummary(req.sheetId);
      else if (req.agent === 'wl_ticket_preflight') res = 'ticketId' in req ? await ai.ticketPreflight(req.ticketId) : await ai.jobPreflight(req.jobId);
      else if (req.agent === 'wl_service_report_draft') res = await ai.serviceReportDraft(req.reportId);
      else res = await ai.complianceDigest();
      return unwrap<WlAiRunResult>(res.data);
    },
    onSuccess: () => invalidateWl(qc),
  });
}

export function useReviewWlAi() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (req: { id: string; action: 'approve'; edit?: WlAiApproveBody } | { id: string; action: 'dismiss' }) => {
      const res = req.action === 'approve'
        ? await api.wirelineSlickline.ai.approve(req.id, req.edit ?? {})
        : await api.wirelineSlickline.ai.dismiss(req.id);
      return unwrap<WlAiStoredRun>(res.data);
    },
    onSettled: () => invalidateWl(qc),
  });
}

// ─── Required crew certificates (WL-6, 0627) ─────────────────────────────────

export const wlRequiredCertsKey = ['wirelineSlickline', 'requiredCertificates'] as const;

export function useWlRequiredCertificates(enabled = true) {
  return useQuery({
    queryKey: wlRequiredCertsKey,
    queryFn: async () => unwrap<WlRequiredCertificates>((await api.wirelineSlickline.requiredCertificates.get()).data),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useUpdateWlRequiredCertificates() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (certificates: string[]) =>
      unwrap<WlRequiredCertificates>((await api.wirelineSlickline.requiredCertificates.update(certificates)).data),
    onSuccess: () => invalidateWl(qc),
  });
}
